import React from 'react'
import { Box, Typography } from '@mui/material'

export interface CleartripFormattedTransaction {
    id: string
    source: string
    transactionDate: string
    amount: number
    currency: string
    txnType?: string // 'CREDIT' or 'DEBIT'
}

interface CleartripTransactionsProps {
    transactions: CleartripFormattedTransaction[]
    hasMoreTransactions: boolean
    isFetchingMore: boolean
    onLoadMore: () => void
    formatAmount: (amount: number, currency: string) => string
    transactionHistoryLabel: string
    noTransactionsLabel: string
    loadMoreLabel: string
    loadingLabel: string
    isRtl?: boolean
}

const CleartripTransactions: React.FC<CleartripTransactionsProps> = ({
    transactions,
    hasMoreTransactions,
    isFetchingMore,
    onLoadMore,
    formatAmount,
    transactionHistoryLabel,
    noTransactionsLabel,
    loadMoreLabel,
    loadingLabel,
    isRtl = false
}) => {
    return (
        <Box sx={{ direction: isRtl ? 'rtl' : 'ltr' }}>
            {/* Transactions History Heading */}
            <Box sx={{ pt: 3, pb: 1 }}>
                <Typography
                    variant="body1"
                    sx={{
                        fontWeight: 600,
                        fontSize: '1rem',
                        lineHeight: '1.5rem',
                        color: 'text.primary'
                    }}
                >
                    {transactionHistoryLabel}
                </Typography>
            </Box>

            {/* Transaction List */}
            {transactions.length === 0 ? (
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        lineHeight: '1.25rem',
                        mt: 2,
                        marginInlineStart: 0.5
                    }}
                >
                    {noTransactionsLabel}
                </Typography>
            ) : (
                <>
                    {transactions.map((txn, index) => {
                        const isCredit = txn.txnType === 'CREDIT'
                        return (
                            <Box
                                key={`${txn.id}-${index}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    py: 2,
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    minHeight: 72
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0, marginInlineEnd: 2 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '0.875rem',
                                            lineHeight: '1.25rem',
                                            color: 'text.primary',
                                            mb: 0.5
                                        }}
                                    >
                                        {txn.source}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            fontSize: '0.75rem',
                                            lineHeight: '1rem',
                                            display: 'block'
                                        }}
                                    >
                                        {txn.transactionDate}
                                    </Typography>
                                </Box>
                                <Box sx={{ flexShrink: 0, minWidth: 80, textAlign: 'end' }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: '0.875rem',
                                            lineHeight: '1.25rem',
                                            color: isCredit ? 'success.main' : 'text.primary'
                                        }}
                                    >
                                        {isCredit ? '+' : '-'} {formatAmount(txn.amount, txn.currency)}
                                    </Typography>
                                </Box>
                            </Box>
                        )
                    })}

                    {/* Load More — text link style matching ct-flights-pwa */}
                    {hasMoreTransactions && (
                        <Box sx={{ pt: 2 }}>
                            <Typography
                                onClick={!isFetchingMore ? onLoadMore : undefined}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    lineHeight: '1.75rem',
                                    color: 'primary.main',
                                    cursor: isFetchingMore ? 'default' : 'pointer'
                                }}
                            >
                                {isFetchingMore ? loadingLabel : loadMoreLabel}
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Box>
    )
}

export { CleartripTransactions }
export type { CleartripTransactionsProps }
